import type { DeadlineAssigneeDto } from '@prospero/shared';
import { useTranslation } from 'react-i18next';
import { AssigneeAutocomplete } from '../../../../components/assignee-autocomplete';
import { useConsultantDeadlineAssignees } from '../../../../hooks/consultant/use-consultant-deadline-assignees';
import { withRemovedAssignee } from '../../../../utils/with-removed-assignee';

interface ConsultantDeadlineAssigneeFieldProps {
  tenantIds: readonly string[];
  isPersonalType: boolean;
  hasDueDate: boolean;
  value: string | null;
  onChange: (userId: string | null) => void;
  /** Persona già assegnata: se anonimizzata non è tra i candidati e resta selezionata come "Utente rimosso". */
  currentAssignee?: DeadlineAssigneeDto | null;
}

/** La persona si assegna cliente per cliente: con zero o più clienti il campo resta spento e dice perché. */
export function ConsultantDeadlineAssigneeField({
  tenantIds,
  isPersonalType,
  hasDueDate,
  value,
  onChange,
  currentAssignee = null,
}: ConsultantDeadlineAssigneeFieldProps) {
  const { t } = useTranslation();
  const singleTenantId = tenantIds.length === 1 ? tenantIds[0] : '';
  const { assigneeCandidates, isLoading, isError } = useConsultantDeadlineAssignees(
    singleTenantId,
    { enabled: isPersonalType },
  );

  if (!isPersonalType) {
    return null;
  }

  return (
    <AssigneeAutocomplete
      value={value}
      candidates={withRemovedAssignee(
        assigneeCandidates,
        currentAssignee,
        t('documents.deadlines.assignee.removedUser'),
      )}
      isLoading={isLoading}
      isError={isError}
      onChange={onChange}
      disabled={singleTenantId === '' || !hasDueDate}
      helperText={resolveHelper({ clientCount: tenantIds.length, hasDueDate, t })}
      testId="consultant-deadline-assignee-field"
    />
  );
}

interface HelperInput {
  clientCount: number;
  hasDueDate: boolean;
  t: (key: string) => string;
}

function resolveHelper({ clientCount, hasDueDate, t }: HelperInput): string {
  if (clientCount === 0) {
    return t('documents.deadlines.assignee.needsClient');
  }
  if (clientCount > 1) {
    return t('documents.deadlines.assignee.perClientHelper');
  }
  return t(
    hasDueDate
      ? 'documents.deadlines.assignee.helper'
      : 'documents.deadlines.assignee.needsDueDate',
  );
}
